/**
 * @file Core constants — zero dependencies.
 * @module tree/constants
 * @license AGPL-3.0-only
 */

// Coordinate spaces
const WORLD  = 'WORLD';
const EYE    = 'EYE';
const NDC    = 'NDC';
const SCREEN = 'SCREEN';
const MODEL  = 'MODEL';
const MATRIX = 'MATRIX';

// NDC Z convention (only difference between backends)
const WEBGL  = -1;   // z ∈ [−1, 1]
const WEBGPU =  0;   // z ∈ [0, 1]

// Visibility results
const INVISIBLE   = 0;
const VISIBLE     = 1;
const SEMIVISIBLE = 2;

// Basis vectors (frozen plain arrays — duck-typed Vec3)
const ORIGIN = Object.freeze([0, 0, 0]);
const i  = Object.freeze([1, 0, 0]);
const j  = Object.freeze([0, 1, 0]);
const k  = Object.freeze([0, 0, 1]);
const _i = Object.freeze([-1, 0, 0]);
const _j = Object.freeze([0, -1, 0]);
const _k = Object.freeze([0, 0, -1]);

/**
 * @file Pure numeric math — mat4, mat3, projection queries, space transforms.
 * @module tree/math
 * @license AGPL-3.0-only
 *
 * CONVENTIONS (all functions in this module follow these):
 *
 *   Storage:    Column-major Float32Array / ArrayLike<number>.
 *               Element [col*4 + row] = M[row, col].
 *
 *   Multiply:   mat4Mul(out, A, B) = A · B   (standard math order).
 *
 *   Pipeline:   clip = P · V · M · v
 *               P = projection (eye → clip)
 *               V = view       (world → eye)
 *               M = model      (local → world)
 *
 *   PV:         All functions expecting a "pv" matrix receive P · V.
 *               This is what _worldToScreen, _ensurePV, etc. compute.
 *
 *   Matrix stack (translate/rotate/scale in p5):
 *     Each call post-multiplies: M = M · T, so:
 *       translate(tx,ty,tz); rotateY(a); scale(s);
 *     yields M = T · R · S. A vertex v is transformed as M·v = T·R·S·v
 *     (scaled first, then rotated, then translated — last-written-first-applied).
 *
 *   p5 bridge note (for implementors of host layers):
 *     p5.Matrix.mult(B) computes  B · this  (pre-multiply, arg on LEFT).
 *     p5 translate/rotate/scale do this · T  (post-multiply, GL stack).
 *     So p5's pvMatrix() = V.clone().mult(P) = P · V — same as ours.
 *     The bridge extracts .mat4 (Float32Array) and feeds it directly,
 *     or uses mat4Mul(out, proj, view) for the non-cached path.
 *
 * Every function uses only stack locals for intermediates (zero shared state).
 * Every mutating function writes to a caller-provided `out` and returns `out`.
 * Returns null on degeneracy (singular matrix, etc.).
 */


// ═══════════════════════════════════════════════════════════════════════════
// Mat4 math
// ═══════════════════════════════════════════════════════════════════════════

/** out = A · B  (column-major, standard math order) */
function mat4Mul(out, A, B) {
  const a0=A[0],a1=A[1],a2=A[2],a3=A[3],
        a4=A[4],a5=A[5],a6=A[6],a7=A[7],
        a8=A[8],a9=A[9],a10=A[10],a11=A[11],
        a12=A[12],a13=A[13],a14=A[14],a15=A[15];
  let b0=B[0],b1=B[1],b2=B[2],b3=B[3];
  out[0]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[1]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[2]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[3]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[4];b1=B[5];b2=B[6];b3=B[7];
  out[4]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[5]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[6]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[7]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[8];b1=B[9];b2=B[10];b3=B[11];
  out[8]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[9]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[10]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[11]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[12];b1=B[13];b2=B[14];b3=B[15];
  out[12]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[13]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[14]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[15]=a3*b0+a7*b1+a11*b2+a15*b3;
  return out;
}

/** out = inverse(src).  Returns null if singular. */
function mat4Invert(out, src) {
  const s=src;
  const a00=s[0],a01=s[1],a02=s[2],a03=s[3],
        a10=s[4],a11=s[5],a12=s[6],a13=s[7],
        a20=s[8],a21=s[9],a22=s[10],a23=s[11],
        a30=s[12],a31=s[13],a32=s[14],a33=s[15];
  const b00=a00*a11-a01*a10,b01=a00*a12-a02*a10,
        b02=a00*a13-a03*a10,b03=a01*a12-a02*a11,
        b04=a01*a13-a03*a11,b05=a02*a13-a03*a12,
        b06=a20*a31-a21*a30,b07=a20*a32-a22*a30,
        b08=a20*a33-a23*a30,b09=a21*a32-a22*a31,
        b10=a21*a33-a23*a31,b11=a22*a33-a23*a32;
  let det=b00*b11-b01*b10+b02*b09+b03*b08-b04*b07+b05*b06;
  if (Math.abs(det) < 1e-12) return null;
  det=1/det;
  out[0]=(a11*b11-a12*b10+a13*b09)*det;
  out[1]=(a02*b10-a01*b11-a03*b09)*det;
  out[2]=(a31*b05-a32*b04+a33*b03)*det;
  out[3]=(a22*b04-a21*b05-a23*b03)*det;
  out[4]=(a12*b08-a10*b11-a13*b07)*det;
  out[5]=(a00*b11-a02*b08+a03*b07)*det;
  out[6]=(a32*b02-a30*b05-a33*b01)*det;
  out[7]=(a20*b05-a22*b02+a23*b01)*det;
  out[8]=(a10*b10-a11*b08+a13*b06)*det;
  out[9]=(a01*b08-a00*b10-a03*b06)*det;
  out[10]=(a30*b04-a31*b02+a33*b00)*det;
  out[11]=(a21*b02-a20*b04-a23*b00)*det;
  out[12]=(a11*b07-a10*b09-a12*b06)*det;
  out[13]=(a00*b09-a01*b07+a02*b06)*det;
  out[14]=(a31*b01-a30*b03-a32*b00)*det;
  out[15]=(a20*b03-a21*b01+a22*b00)*det;
  return out;
}

/** out = transpose(src) */
function mat4Transpose(out, src) {
  if (out === src) {
    let t;
    t=src[1];out[1]=src[4];out[4]=t;
    t=src[2];out[2]=src[8];out[8]=t;
    t=src[3];out[3]=src[12];out[12]=t;
    t=src[6];out[6]=src[9];out[9]=t;
    t=src[7];out[7]=src[13];out[13]=t;
    t=src[11];out[11]=src[14];out[14]=t;
  } else {
    out[0]=src[0];out[1]=src[4];out[2]=src[8];out[3]=src[12];
    out[4]=src[1];out[5]=src[5];out[6]=src[9];out[7]=src[13];
    out[8]=src[2];out[9]=src[6];out[10]=src[10];out[11]=src[14];
    out[12]=src[3];out[13]=src[7];out[14]=src[11];out[15]=src[15];
  }
  return out;
}

/** out[0..8] = inverseTranspose(upper3×3(src))  (normal matrix) */
function mat3NormalFromMat4(out, src) {
  const a00=src[0],a01=src[1],a02=src[2],
        a10=src[4],a11=src[5],a12=src[6],
        a20=src[8],a21=src[9],a22=src[10];
  const b01=a22*a11-a12*a21,
        b11=-a22*a01+a02*a21,
        b21=a12*a01-a02*a11;
  let det=a00*b01+a10*b11+a20*b21;
  if (Math.abs(det) < 1e-12) { for(let i=0;i<9;i++)out[i]=0; return out; }
  det=1/det;
  out[0]=b01*det;
  out[1]=(-a22*a10+a12*a20)*det;
  out[2]=(a21*a10-a11*a20)*det;
  out[3]=b11*det;
  out[4]=(a22*a00-a02*a20)*det;
  out[5]=(-a21*a00+a01*a20)*det;
  out[6]=b21*det;
  out[7]=(-a12*a00+a02*a10)*det;
  out[8]=(a11*a00-a01*a10)*det;
  return out;
}

/** out = mat4 * [x,y,z,1], perspective-divides, writes xyz */
function mat4MulPoint(out, m, x, y, z) {
  const rx = m[0]*x + m[4]*y + m[8]*z  + m[12];
  const ry = m[1]*x + m[5]*y + m[9]*z  + m[13];
  const rz = m[2]*x + m[6]*y + m[10]*z + m[14];
  const rw = m[3]*x + m[7]*y + m[11]*z + m[15];
  if (rw !== 0 && rw !== 1) {
    out[0] = rx/rw; out[1] = ry/rw; out[2] = rz/rw;
  } else {
    out[0] = rx; out[1] = ry; out[2] = rz;
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Projection queries  (read scalars from a projection mat4)
// ═══════════════════════════════════════════════════════════════════════════

/** @returns {boolean} true if orthographic */
function projIsOrtho(p) { return p[15] !== 0; }

/**
 * Near plane distance.
 * @param {ArrayLike<number>} p  Projection Mat4.
 * @param {number} ndcZMin  WEBGL (−1) or WEBGPU (0).
 */
function projNear(p, ndcZMin) {
  return p[15] === 0
    ? p[14] / (p[10] + ndcZMin)
    : (p[14] - ndcZMin) / p[10];
}

/** Far plane distance (convention-independent: far always maps to NDC z=1). */
function projFar(p) {
  return p[15] === 0
    ? p[14] / (1 + p[10])
    : (p[14] - 1) / p[10];
}

function projLeft(p, ndcZMin) {
  return p[15] === 1
    ? -(1 + p[12]) / p[0]
    : projNear(p, ndcZMin) * (p[8] - 1) / p[0];
}

function projRight(p, ndcZMin) {
  return p[15] === 1
    ? (1 - p[12]) / p[0]
    : projNear(p, ndcZMin) * (1 + p[8]) / p[0];
}

function projTop(p, ndcZMin) {
  return p[15] === 1
    ? (p[13] - 1) / p[5]
    : projNear(p, ndcZMin) * (p[9] - 1) / p[5];
}

function projBottom(p, ndcZMin) {
  return p[15] === 1
    ? (1 + p[13]) / p[5]
    : projNear(p, ndcZMin) * (1 + p[9]) / p[5];
}

/** Vertical fov (radians, perspective only). */
function projFov(p) {
  return Math.abs(2 * Math.atan(1 / p[5]));
}

/** Horizontal fov (radians, perspective only). */
function projHfov(p) {
  return Math.abs(2 * Math.atan(1 / p[0]));
}

// ═══════════════════════════════════════════════════════════════════════════
// Derived matrices (convenience)
// ═══════════════════════════════════════════════════════════════════════════

/** out = P · V */
function mat4PV(out, proj, view) { return mat4Mul(out, proj, view); }

/** out = V · M */
function mat4MV(out, model, view) { return mat4Mul(out, view, model); }

// ═══════════════════════════════════════════════════════════════════════════
// Location / Direction transforms
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Relative transform for locations (points): out = inv(to) · from.
 * @param {ArrayLike<number>} out   16-element destination.
 * @param {ArrayLike<number>} from  Source frame transform.
 * @param {ArrayLike<number>} to    Destination frame transform.
 * @returns {ArrayLike<number>|null} out, or null if to is singular.
 */
function mat4Location(out, from, to) {
  return mat4Invert(out, to) && mat4Mul(out, out, from);
}

/**
 * Relative transform for directions (vectors): out = to₃ · inv(from₃).
 * Uses only the upper-left 3×3 blocks, ignoring translation.
 * @param {ArrayLike<number>} out   9-element destination.
 * @param {ArrayLike<number>} from  Source frame transform.
 * @param {ArrayLike<number>} to    Destination frame transform.
 * @returns {ArrayLike<number>|null} out, or null if from is singular.
 */
function mat3Direction(out, from, to) {
  const a00=from[0], a01=from[1], a02=from[2],
        a10=from[4], a11=from[5], a12=from[6],
        a20=from[8], a21=from[9], a22=from[10];
  const b01=a22*a11-a12*a21,
        b11=a12*a20-a22*a10,
        b21=a21*a10-a11*a20;
  let det=a00*b01+a01*b11+a02*b21;
  if (Math.abs(det) < 1e-12) return null;
  det=1/det;
  const i00=b01*det, i01=(a02*a21-a22*a01)*det, i02=(a12*a01-a02*a11)*det;
  const i10=b11*det, i11=(a22*a00-a02*a20)*det, i12=(a02*a10-a12*a00)*det;
  const i20=b21*det, i21=(a01*a20-a21*a00)*det, i22=(a11*a00-a01*a10)*det;
  const t00=to[0], t01=to[1], t02=to[2],
        t10=to[4], t11=to[5], t12=to[6],
        t20=to[8], t21=to[9], t22=to[10];
  const m00=t00*i00+t10*i01+t20*i02, m01=t01*i00+t11*i01+t21*i02, m02=t02*i00+t12*i01+t22*i02;
  const m10=t00*i10+t10*i11+t20*i12, m11=t01*i10+t11*i11+t21*i12, m12=t02*i10+t12*i11+t22*i12;
  const m20=t00*i20+t10*i21+t20*i22, m21=t01*i20+t11*i21+t21*i22, m22=t02*i20+t12*i21+t22*i22;
  out[0]=m00; out[1]=m10; out[2]=m20;
  out[3]=m01; out[4]=m11; out[5]=m21;
  out[6]=m02; out[7]=m12; out[8]=m22;
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Space transforms — mapLocation / mapDirection
// ═══════════════════════════════════════════════════════════════════════════
//
// FLAT DISPATCH: every from→to pair is a self-contained leaf.
// No path calls back into mapLocation/mapDirection (no reentrancy).
// All intermediates are stack locals (zero shared state).
//
// Matrices bag m:
//   {
//     pMatrix:   Float32Array(16)  — projection (eye → clip)
//     vMatrix:   Float32Array(16)  — view        (world → eye)
//     eMatrix?:  Float32Array(16)  — eye         (eye → world, inv view); lazy
//     pvMatrix?: Float32Array(16)  — P · V;      lazy
//     ipvMatrix?:Float32Array(16)  — inv(P · V); lazy
//     fromFrame?:Float32Array(16)  — MATRIX source frame (custom space)
//     toFrameInv?:Float32Array(16) — inv(MATRIX dest frame)
//   }
//

// ── Location leaf helpers ────────────────────────────────────────────────

function _worldToScreen(out, px, py, pz, pv, vp, ndcZMin) {
  const x = pv[0]*px+pv[4]*py+pv[8]*pz+pv[12];
  const y = pv[1]*px+pv[5]*py+pv[9]*pz+pv[13];
  const z = pv[2]*px+pv[6]*py+pv[10]*pz+pv[14];
  const w = pv[3]*px+pv[7]*py+pv[11]*pz+pv[15];
  const xi = (w !== 0 && w !== 1) ? x/w : x;
  const yi = (w !== 0 && w !== 1) ? y/w : y;
  const zi = (w !== 0 && w !== 1) ? z/w : z;
  const ndcZRange = 1 - ndcZMin;
  out[0] = (xi*0.5+0.5)*vp[2]+vp[0];
  out[1] = (yi*0.5+0.5)*vp[3]+vp[1];
  out[2] = (zi - ndcZMin) / ndcZRange;
  return out;
}

function _screenToWorld(out, px, py, pz, ipv, vp, ndcZMin) {
  const ndcZRange = 1 - ndcZMin;
  const nx = ((px-vp[0])/vp[2])*2-1;
  const ny = ((py-vp[1])/vp[3])*2-1;
  const nz = pz * ndcZRange + ndcZMin;
  const x = ipv[0]*nx+ipv[4]*ny+ipv[8]*nz+ipv[12];
  const y = ipv[1]*nx+ipv[5]*ny+ipv[9]*nz+ipv[13];
  const z = ipv[2]*nx+ipv[6]*ny+ipv[10]*nz+ipv[14];
  const w = ipv[3]*nx+ipv[7]*ny+ipv[11]*nz+ipv[15];
  out[0]=x/w; out[1]=y/w; out[2]=z/w;
  return out;
}

function _worldToNDC(out, px, py, pz, pv) {
  const x = pv[0]*px+pv[4]*py+pv[8]*pz+pv[12];
  const y = pv[1]*px+pv[5]*py+pv[9]*pz+pv[13];
  const z = pv[2]*px+pv[6]*py+pv[10]*pz+pv[14];
  const w = pv[3]*px+pv[7]*py+pv[11]*pz+pv[15];
  out[0]=x/w; out[1]=y/w; out[2]=z/w;
  return out;
}

function _ndcToWorld(out, px, py, pz, ipv) {
  const x = ipv[0]*px+ipv[4]*py+ipv[8]*pz+ipv[12];
  const y = ipv[1]*px+ipv[5]*py+ipv[9]*pz+ipv[13];
  const z = ipv[2]*px+ipv[6]*py+ipv[10]*pz+ipv[14];
  const w = ipv[3]*px+ipv[7]*py+ipv[11]*pz+ipv[15];
  out[0]=x/w; out[1]=y/w; out[2]=z/w;
  return out;
}

function _screenToNDC(out, px, py, pz, vp, ndcZMin) {
  const ndcZRange = 1 - ndcZMin;
  out[0] = ((px-vp[0])/vp[2])*2-1;
  out[1] = ((py-vp[1])/vp[3])*2-1;
  out[2] = pz * ndcZRange + ndcZMin;
  return out;
}

function _ndcToScreen(out, px, py, pz, vp, ndcZMin) {
  const ndcZRange = 1 - ndcZMin;
  out[0] = (px*0.5+0.5)*vp[2]+vp[0];
  out[1] = (py*0.5+0.5)*vp[3]+vp[1];
  out[2] = (pz - ndcZMin) / ndcZRange;
  return out;
}

// ── _ensurePV — return pvMatrix from bag, computing inline if absent ──────

function _ensurePV(m) {
  if (m.pvMatrix) return m.pvMatrix;
  const p = m.pMatrix, v = m.vMatrix;
  return [
    p[0]*v[0]+p[4]*v[1]+p[8]*v[2]+p[12]*v[3],
    p[1]*v[0]+p[5]*v[1]+p[9]*v[2]+p[13]*v[3],
    p[2]*v[0]+p[6]*v[1]+p[10]*v[2]+p[14]*v[3],
    p[3]*v[0]+p[7]*v[1]+p[11]*v[2]+p[15]*v[3],
    p[0]*v[4]+p[4]*v[5]+p[8]*v[6]+p[12]*v[7],
    p[1]*v[4]+p[5]*v[5]+p[9]*v[6]+p[13]*v[7],
    p[2]*v[4]+p[6]*v[5]+p[10]*v[6]+p[14]*v[7],
    p[3]*v[4]+p[7]*v[5]+p[11]*v[6]+p[15]*v[7],
    p[0]*v[8]+p[4]*v[9]+p[8]*v[10]+p[12]*v[11],
    p[1]*v[8]+p[5]*v[9]+p[9]*v[10]+p[13]*v[11],
    p[2]*v[8]+p[6]*v[9]+p[10]*v[10]+p[14]*v[11],
    p[3]*v[8]+p[7]*v[9]+p[11]*v[10]+p[15]*v[11],
    p[0]*v[12]+p[4]*v[13]+p[8]*v[14]+p[12]*v[15],
    p[1]*v[12]+p[5]*v[13]+p[9]*v[14]+p[13]*v[15],
    p[2]*v[12]+p[6]*v[13]+p[10]*v[14]+p[14]*v[15],
    p[3]*v[12]+p[7]*v[13]+p[11]*v[14]+p[15]*v[15],
  ];
}

/**
 * Map a point between coordinate spaces.
 *
 * @param {Vec3}   out         Result written here.
 * @param {number} px,py,pz    Input point.
 * @param {string} from        Source space constant.
 * @param {string} to          Target space constant.
 * @param {object} m           Matrices bag:
 *   { pMatrix, vMatrix, eMatrix?, pvMatrix?, ipvMatrix?, fromFrame?, toFrameInv? }
 * @param {Vec4}   vp          Viewport [x, y, width, height].
 * @param {number} ndcZMin     WEBGL (−1) or WEBGPU (0).
 */
function mapLocation(out, px, py, pz, from, to, m, vp, ndcZMin) {
  // WORLD ↔ SCREEN
  if (from === WORLD && to === SCREEN)
    return _worldToScreen(out, px,py,pz, _ensurePV(m), vp, ndcZMin);
  if (from === SCREEN && to === WORLD)
    return _screenToWorld(out, px,py,pz, m.ipvMatrix, vp, ndcZMin);

  // WORLD ↔ NDC
  if (from === WORLD && to === NDC)
    return _worldToNDC(out, px,py,pz, _ensurePV(m));
  if (from === NDC && to === WORLD)
    return _ndcToWorld(out, px,py,pz, m.ipvMatrix);

  // SCREEN ↔ NDC
  if (from === SCREEN && to === NDC)
    return _screenToNDC(out, px,py,pz, vp, ndcZMin);
  if (from === NDC && to === SCREEN)
    return _ndcToScreen(out, px,py,pz, vp, ndcZMin);

  // WORLD ↔ EYE
  if (from === WORLD && to === EYE)
    return mat4MulPoint(out, m.vMatrix, px,py,pz);
  if (from === EYE && to === WORLD)
    return mat4MulPoint(out, m.eMatrix, px,py,pz);

  // EYE ↔ SCREEN
  if (from === EYE && to === SCREEN) {
    const e = m.eMatrix;
    const ex=e[0]*px+e[4]*py+e[8]*pz+e[12],
          ey=e[1]*px+e[5]*py+e[9]*pz+e[13],
          ez=e[2]*px+e[6]*py+e[10]*pz+e[14];
    return _worldToScreen(out, ex,ey,ez, _ensurePV(m), vp, ndcZMin);
  }
  if (from === SCREEN && to === EYE) {
    _screenToWorld(out, px,py,pz, m.ipvMatrix, vp, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return mat4MulPoint(out, m.vMatrix, wx,wy,wz);
  }

  // EYE ↔ NDC
  if (from === EYE && to === NDC) {
    const e = m.eMatrix;
    const ex=e[0]*px+e[4]*py+e[8]*pz+e[12],
          ey=e[1]*px+e[5]*py+e[9]*pz+e[13],
          ez=e[2]*px+e[6]*py+e[10]*pz+e[14];
    return _worldToNDC(out, ex,ey,ez, _ensurePV(m));
  }
  if (from === NDC && to === EYE) {
    _ndcToWorld(out, px,py,pz, m.ipvMatrix);
    const wx=out[0],wy=out[1],wz=out[2];
    return mat4MulPoint(out, m.vMatrix, wx,wy,wz);
  }

  // MATRIX (custom frame) ↔ WORLD
  if (from === MATRIX && to === WORLD)
    return mat4MulPoint(out, m.fromFrame, px,py,pz);
  if (from === WORLD && to === MATRIX)
    return mat4MulPoint(out, m.toFrameInv, px,py,pz);

  // MATRIX ↔ EYE
  if (from === MATRIX && to === EYE) {
    const f = m.fromFrame;
    const fx=f[0]*px+f[4]*py+f[8]*pz+f[12],
          fy=f[1]*px+f[5]*py+f[9]*pz+f[13],
          fz=f[2]*px+f[6]*py+f[10]*pz+f[14];
    return mat4MulPoint(out, m.vMatrix, fx,fy,fz);
  }
  if (from === EYE && to === MATRIX) {
    const e = m.eMatrix;
    const ex=e[0]*px+e[4]*py+e[8]*pz+e[12],
          ey=e[1]*px+e[5]*py+e[9]*pz+e[13],
          ez=e[2]*px+e[6]*py+e[10]*pz+e[14];
    return mat4MulPoint(out, m.toFrameInv, ex,ey,ez);
  }

  // MATRIX ↔ SCREEN
  if (from === MATRIX && to === SCREEN) {
    const f = m.fromFrame;
    const fx=f[0]*px+f[4]*py+f[8]*pz+f[12],
          fy=f[1]*px+f[5]*py+f[9]*pz+f[13],
          fz=f[2]*px+f[6]*py+f[10]*pz+f[14];
    return _worldToScreen(out, fx,fy,fz, _ensurePV(m), vp, ndcZMin);
  }
  if (from === SCREEN && to === MATRIX) {
    _screenToWorld(out, px,py,pz, m.ipvMatrix, vp, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return mat4MulPoint(out, m.toFrameInv, wx,wy,wz);
  }

  // MATRIX ↔ NDC
  if (from === MATRIX && to === NDC) {
    const f = m.fromFrame;
    const fx=f[0]*px+f[4]*py+f[8]*pz+f[12],
          fy=f[1]*px+f[5]*py+f[9]*pz+f[13],
          fz=f[2]*px+f[6]*py+f[10]*pz+f[14];
    return _worldToNDC(out, fx,fy,fz, _ensurePV(m));
  }
  if (from === NDC && to === MATRIX) {
    _ndcToWorld(out, px,py,pz, m.ipvMatrix);
    const wx=out[0],wy=out[1],wz=out[2];
    return mat4MulPoint(out, m.toFrameInv, wx,wy,wz);
  }

  // MATRIX ↔ MATRIX
  if (from === MATRIX && to === MATRIX) {
    const f = m.fromFrame;
    const fx=f[0]*px+f[4]*py+f[8]*pz+f[12],
          fy=f[1]*px+f[5]*py+f[9]*pz+f[13],
          fz=f[2]*px+f[6]*py+f[10]*pz+f[14];
    return mat4MulPoint(out, m.toFrameInv, fx,fy,fz);
  }

  // Fallback
  out[0]=px; out[1]=py; out[2]=pz;
  return out;
}

// ── Direction helpers ────────────────────────────────────────────────────

/** Apply the 3×3 linear part of a mat4 (rotation/scale, no translation). */
function _applyDir(out, mat, dx, dy, dz) {
  out[0]=mat[0]*dx+mat[4]*dy+mat[8]*dz;
  out[1]=mat[1]*dx+mat[5]*dy+mat[9]*dz;
  out[2]=mat[2]*dx+mat[6]*dy+mat[10]*dz;
  return out;
}

function _worldToScreenDir(out, dx, dy, dz, proj, view, vpW, vpH, ndcZMin) {
  const edx = view[0]*dx + view[4]*dy + view[8]*dz;
  const edy = view[1]*dx + view[5]*dy + view[9]*dz;
  const edz = view[2]*dx + view[6]*dy + view[10]*dz;
  const isPersp = proj[15] === 0;
  let sdx = edx, sdy = edy;
  if (isPersp) {
    const zEye = view[14];
    const halfTan = Math.tan(projFov(proj) / 2);
    const k = Math.abs(zEye * halfTan);
    const pixPerUnit = vpH / (2 * k);
    sdx *= pixPerUnit;
    sdy *= pixPerUnit;
  } else {
    const orthoW = Math.abs(projRight(proj, ndcZMin) - projLeft(proj, ndcZMin));
    sdx *= vpW / orthoW;
    sdy *= vpH / Math.abs(projTop(proj, ndcZMin) - projBottom(proj, ndcZMin));
  }
  const near = projNear(proj, ndcZMin), far = projFar(proj);
  const depthRange = near - far;
  let sdz;
  if (isPersp) {
    sdz = edz / (depthRange / Math.tan(projFov(proj) / 2));
  } else {
    sdz = edz / (depthRange / (Math.abs(projRight(proj, ndcZMin) - projLeft(proj, ndcZMin)) / vpW));
  }
  out[0] = sdx; out[1] = sdy; out[2] = sdz;
  return out;
}

function _screenToWorldDir(out, dx, dy, dz, proj, view, eye, vpW, vpH, ndcZMin) {
  const isPersp = proj[15] === 0;
  let edx = dx, edy = dy;
  if (isPersp) {
    const zEye = view[14];
    const halfTan = Math.tan(projFov(proj) / 2);
    const k = Math.abs(zEye * halfTan);
    edx *= 2 * k / vpH;
    edy *= 2 * k / vpH;
  } else {
    const orthoW = Math.abs(projRight(proj, ndcZMin) - projLeft(proj, ndcZMin));
    edx *= orthoW / vpW;
    edy *= Math.abs(projTop(proj, ndcZMin) - projBottom(proj, ndcZMin)) / vpH;
  }
  const near = projNear(proj, ndcZMin), far = projFar(proj);
  const depthRange = near - far;
  let edz;
  if (isPersp) {
    edz = dz * (depthRange / Math.tan(projFov(proj) / 2));
  } else {
    edz = dz * (depthRange / (Math.abs(projRight(proj, ndcZMin) - projLeft(proj, ndcZMin)) / vpW));
  }
  _applyDir(out, eye, edx, edy, edz);
  return out;
}

function _screenToNDCDir(out, dx, dy, dz, vpW, vpH, ndcZMin) {
  const ndcZRange = 1 - ndcZMin;
  out[0] = 2 * dx / vpW;
  out[1] = 2 * dy / vpH;
  out[2] = dz * ndcZRange;
  return out;
}

function _ndcToScreenDir(out, dx, dy, dz, vpW, vpH, ndcZMin) {
  const ndcZRange = 1 - ndcZMin;
  out[0] = vpW * dx / 2;
  out[1] = vpH * dy / 2;
  out[2] = dz / ndcZRange;
  return out;
}

/**
 * Map a direction vector between coordinate spaces.
 * Same bag contract as mapLocation.
 */
function mapDirection(out, dx, dy, dz, from, to, m, vp, ndcZMin) {
  const vpW = Math.abs(vp[2]), vpH = Math.abs(vp[3]);

  // EYE ↔ WORLD (most common)
  if (from === EYE && to === WORLD) return _applyDir(out, m.eMatrix, dx, dy, dz);
  if (from === WORLD && to === EYE) return _applyDir(out, m.vMatrix, dx, dy, dz);

  // WORLD ↔ SCREEN
  if (from === WORLD && to === SCREEN)
    return _worldToScreenDir(out, dx,dy,dz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
  if (from === SCREEN && to === WORLD)
    return _screenToWorldDir(out, dx,dy,dz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);

  // SCREEN ↔ NDC
  if (from === SCREEN && to === NDC)
    return _screenToNDCDir(out, dx,dy,dz, vpW, vpH, ndcZMin);
  if (from === NDC && to === SCREEN)
    return _ndcToScreenDir(out, dx,dy,dz, vpW, vpH, ndcZMin);

  // WORLD ↔ NDC
  if (from === WORLD && to === NDC) {
    _worldToScreenDir(out, dx,dy,dz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    return _screenToNDCDir(out, sx,sy,sz, vpW, vpH, ndcZMin);
  }
  if (from === NDC && to === WORLD) {
    _ndcToScreenDir(out, dx,dy,dz, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    return _screenToWorldDir(out, sx,sy,sz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);
  }

  // EYE ↔ SCREEN
  if (from === EYE && to === SCREEN) {
    _applyDir(out, m.eMatrix, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    return _worldToScreenDir(out, wx,wy,wz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
  }
  if (from === SCREEN && to === EYE) {
    _screenToWorldDir(out, dx,dy,dz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.vMatrix, wx,wy,wz);
  }

  // EYE ↔ NDC
  if (from === EYE && to === NDC) {
    _applyDir(out, m.eMatrix, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    _worldToScreenDir(out, wx,wy,wz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    return _screenToNDCDir(out, sx,sy,sz, vpW, vpH, ndcZMin);
  }
  if (from === NDC && to === EYE) {
    _ndcToScreenDir(out, dx,dy,dz, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    _screenToWorldDir(out, sx,sy,sz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.vMatrix, wx,wy,wz);
  }

  // MATRIX ↔ WORLD
  if (from === MATRIX && to === WORLD) return _applyDir(out, m.fromFrame, dx,dy,dz);
  if (from === WORLD && to === MATRIX) return _applyDir(out, m.toFrameInv, dx,dy,dz);

  // MATRIX ↔ EYE
  if (from === MATRIX && to === EYE) {
    _applyDir(out, m.fromFrame, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.vMatrix, wx,wy,wz);
  }
  if (from === EYE && to === MATRIX) {
    _applyDir(out, m.eMatrix, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.toFrameInv, wx,wy,wz);
  }

  // MATRIX ↔ SCREEN
  if (from === MATRIX && to === SCREEN) {
    _applyDir(out, m.fromFrame, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    return _worldToScreenDir(out, wx,wy,wz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
  }
  if (from === SCREEN && to === MATRIX) {
    _screenToWorldDir(out, dx,dy,dz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.toFrameInv, wx,wy,wz);
  }

  // MATRIX ↔ NDC
  if (from === MATRIX && to === NDC) {
    _applyDir(out, m.fromFrame, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    _worldToScreenDir(out, wx,wy,wz, m.pMatrix, m.vMatrix, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    return _screenToNDCDir(out, sx,sy,sz, vpW, vpH, ndcZMin);
  }
  if (from === NDC && to === MATRIX) {
    _ndcToScreenDir(out, dx,dy,dz, vpW, vpH, ndcZMin);
    const sx=out[0],sy=out[1],sz=out[2];
    _screenToWorldDir(out, sx,sy,sz, m.pMatrix, m.vMatrix, m.eMatrix, vpW, vpH, ndcZMin);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.toFrameInv, wx,wy,wz);
  }

  // MATRIX ↔ MATRIX
  if (from === MATRIX && to === MATRIX) {
    _applyDir(out, m.fromFrame, dx,dy,dz);
    const wx=out[0],wy=out[1],wz=out[2];
    return _applyDir(out, m.toFrameInv, wx,wy,wz);
  }

  // Fallback
  out[0]=dx; out[1]=dy; out[2]=dz;
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// pixelRatio
// ═══════════════════════════════════════════════════════════════════════════

/**
 * World-units-per-pixel at a given eye-space Z depth.
 * @param {ArrayLike<number>} proj  Projection mat4.
 * @param {number} vpH      Viewport height (pixels).
 * @param {number} eyeZ     Eye-space Z (negative for in-front-of camera).
 * @param {number} ndcZMin  WEBGL or WEBGPU.
 */
function pixelRatio(proj, vpH, eyeZ, ndcZMin) {
  if (projIsOrtho(proj)) {
    return Math.abs(projTop(proj, ndcZMin) - projBottom(proj, ndcZMin)) / vpH;
  }
  return 2 * Math.abs(eyeZ) * Math.tan(projFov(proj) / 2) / vpH;
}

// ═══════════════════════════════════════════════════════════════════════════
// Pick-matrix
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Apply the pick-matrix in-place:  proj ← M_pick · proj
 *
 * Zooms the frustum so that pixel (px, py) maps to the full NDC square,
 * making a 1×1 framebuffer render contain exactly that pixel's content.
 * Convention-independent — correct for both perspective and orthographic.
 *
 * M_pick (column-major):
 *   [ sx   0   0   tx ]     sx = W,  sy = H
 *   [  0  sy   0   ty ]     cx = NDC X of pixel centre =  2*(px+0.5)/W − 1
 *   [  0   0   1    0 ]     cy = NDC Y of pixel centre =  1 − 2*(py+0.5)/H
 *   [  0   0   0    1 ]     tx = −cx·W,  ty = −cy·H
 *
 * @param {Float32Array} proj  Projection mat4 — mutated in place.
 * @param {number} px  Query X (CSS pixels).
 * @param {number} py  Query Y (CSS pixels).
 * @param {number} W   Canvas width  (CSS pixels).
 * @param {number} H   Canvas height (CSS pixels).
 * @returns {Float32Array} proj (same reference)
 */
function applyPickMatrix(proj, px, py, W, H) {
  const cx =  2 * (px + 0.5) / W - 1;
  const cy = -2 * (py + 0.5) / H + 1;   // Y flip: screen-down → NDC-up
  const sx = W;
  const sy = H;
  const tx = -cx * W;
  const ty = -cy * H;
  // P_pick = M_pick * P_orig  (rows 2 and 3 are unchanged)
  for (let j = 0; j < 4; j++) {
    const a = proj[j * 4];
    const b = proj[j * 4 + 1];
    const d = proj[j * 4 + 3];
    proj[j * 4]     = sx * a + tx * d;
    proj[j * 4 + 1] = sy * b + ty * d;
  }
  return proj;
}

/**
 * @file Pure quaternion/spline math + track state machines.
 * @module tree/track
 * @license AGPL-3.0-only
 *
 * Zero dependencies.  No p5, DOM, WebGL, or WebGPU usage.
 *
 * ── Exports ──────────────────────────────────────────────────────────────────
 *  Quaternion helpers
 *    qSet qCopy qDot qNormalize qNegate qMul qSlerp qNlerp
 *    qFromAxisAngle qFromLookDir qFromRotMat3x3 qFromMat4 qToMat4
 *    quatToAxisAngle
 *  Spline / vector helpers
 *    hermiteVec3  lerpVec3
 *  Transform / mat4 helpers
 *    transformToMat4  mat4ToTransform
 *  Tracks
 *    PoseTrack    — { pos, rot, scl } TRS keyframes
 *    CameraTrack  — { eye, center, up } lookat keyframes
 *
 * ── Class hierarchy ───────────────────────────────────────────────────────────
 *  Track (unexported, never instantiated directly)
 *    └── PoseTrack   (exported)
 *    └── CameraTrack (exported)
 *
 *  Track holds all transport machinery: cursor, play/stop/seek/tick,
 *  hooks, rate semantics.  Subclasses add only keyframe storage and
 *  add() / eval() for their respective data shape.
 *
 * ── Hook architecture ─────────────────────────────────────────────────────────
 *  Lib-space hooks (underscore prefix — reserved for host layer / UI layer):
 *    _onActivate / _onDeactivate  — fire on playing transitions false→true / true→false.
 *    _onPlay / _onEnd / _onStop   — mirror the user-space hooks; used by the UI layer
 *                                   so it can sync without chaining the public slots.
 *
 *  User-space hooks (public):
 *    onPlay : fires in play()  on false→true transition.
 *    onEnd  : fires in tick()  at natural boundary (once mode only).
 *    onStop : fires in stop() / reset() — explicit deactivation.
 *    onEnd and onStop are mutually exclusive per event.
 *
 *  Firing order:
 *    play()  → onPlay → _onPlay → _onActivate
 *    tick()  → onEnd  → _onEnd  → _onDeactivate
 *    stop()  → onStop → _onStop → _onDeactivate
 *    reset() → onStop → _onStop → _onDeactivate
 *
 * ── Loop modes ────────────────────────────────────────────────────────────────
 *  once     loop=false, bounce=false  — stop at end (fires onEnd)
 *  repeat   loop=true,  bounce=false  — wrap back to start
 *  bounce loop=true,  bounce=true   — bounce at boundaries
 *
 *  Exclusivity enforced in play():
 *    bounce: true  → loop is also set true
 *    loop: false     → bounce is also cleared
 *
 * ── Playback semantics (rate + _dir) ─────────────────────────────────────────
 *  rate > 0   forward
 *  rate < 0   backward
 *  rate === 0 frozen: tick() no-op; playing unchanged
 *
 *  play() is the sole setter of playing = true.
 *  stop() is the sole setter of playing = false.
 *  Assigning rate never starts or stops playback.
 *
 *  _dir (internal, ±1) tracks the current bounce travel direction.
 *  tick() advances by rate * _dir and flips _dir at boundaries.
 *  rate always holds the user-set value — it is never mutated by bounce.
 *  _dir is reset to 1 only in reset() (keyframes cleared) — stop/replay
 *  preserves the current travel direction.
 *
 * ── One-keyframe behaviour ────────────────────────────────────────────────────
 *  play() with exactly one keyframe snaps eval() to that keyframe without
 *  setting playing = true and without firing hooks.
 */


// =========================================================================
// S1  Quaternion helpers  (flat [x, y, z, w], w-last)
// =========================================================================

/** Set all four components. @returns {number[]} out */
const qSet = (out, x, y, z, w) => {
  out[0] = x; out[1] = y; out[2] = z; out[3] = w; return out;
};

/** Copy quaternion a into out. @returns {number[]} out */
const qCopy = (out, a) => {
  out[0] = a[0]; out[1] = a[1]; out[2] = a[2]; out[3] = a[3]; return out;
};

/** Dot product of two quaternions. */
const qDot = (a, b) => a[0]*b[0] + a[1]*b[1] + a[2]*b[2] + a[3]*b[3];

/** Normalise quaternion in-place. @returns {number[]} out */
const qNormalize = (out) => {
  const l = Math.sqrt(out[0]*out[0]+out[1]*out[1]+out[2]*out[2]+out[3]*out[3]) || 1;
  out[0]/=l; out[1]/=l; out[2]/=l; out[3]/=l; return out;
};

/** Negate quaternion (same rotation, different hemisphere). @returns {number[]} out */
const qNegate = (out, a) => {
  out[0]=-a[0]; out[1]=-a[1]; out[2]=-a[2]; out[3]=-a[3]; return out;
};

/** Hamilton product out = a * b. @returns {number[]} out */
const qMul = (out, a, b) => {
  const ax=a[0],ay=a[1],az=a[2],aw=a[3], bx=b[0],by=b[1],bz=b[2],bw=b[3];
  out[0]=aw*bx+ax*bw+ay*bz-az*by;
  out[1]=aw*by-ax*bz+ay*bw+az*bx;
  out[2]=aw*bz+ax*by-ay*bx+az*bw;
  out[3]=aw*bw-ax*bx-ay*by-az*bz;
  return out;
};

/** Spherical linear interpolation. @returns {number[]} out */
const qSlerp = (out, a, b, t) => {
  let bx=b[0],by=b[1],bz=b[2],bw=b[3];
  let d = a[0]*bx+a[1]*by+a[2]*bz+a[3]*bw;
  if (d < 0) { bx=-bx; by=-by; bz=-bz; bw=-bw; d=-d; }
  let f0, f1;
  if (1-d > 1e-10) {
    const th=Math.acos(d), st=Math.sin(th);
    f0=Math.sin((1-t)*th)/st; f1=Math.sin(t*th)/st;
  } else {
    f0=1-t; f1=t;
  }
  out[0]=a[0]*f0+bx*f1; out[1]=a[1]*f0+by*f1;
  out[2]=a[2]*f0+bz*f1; out[3]=a[3]*f0+bw*f1;
  return qNormalize(out);
};

/**
 * Normalised linear interpolation (nlerp).
 * Cheaper than slerp; slightly non-constant angular velocity.
 * Handles antipodal quats by flipping b when dot < 0.
 * @returns {number[]} out
 */
const qNlerp = (out, a, b, t) => {
  let bx=b[0],by=b[1],bz=b[2],bw=b[3];
  if (a[0]*bx+a[1]*by+a[2]*bz+a[3]*bw < 0) { bx=-bx; by=-by; bz=-bz; bw=-bw; }
  out[0]=a[0]+t*(bx-a[0]); out[1]=a[1]+t*(by-a[1]);
  out[2]=a[2]+t*(bz-a[2]); out[3]=a[3]+t*(bw-a[3]);
  return qNormalize(out);
};

/**
 * Build a quaternion from axis-angle.
 * @param {number[]} out
 * @param {number} ax @param {number} ay @param {number} az  Axis (need not be unit).
 * @param {number} angle  Radians.
 * @returns {number[]} out
 */
const qFromAxisAngle = (out, ax, ay, az, angle) => {
  const half = angle * 0.5;
  const s    = Math.sin(half);
  const len  = Math.sqrt(ax*ax + ay*ay + az*az) || 1;
  out[0] = s * ax / len; out[1] = s * ay / len; out[2] = s * az / len;
  out[3] = Math.cos(half);
  return out;
};

/**
 * Build a quaternion from a look direction (−Z forward) and optional up (default +Y).
 * @param {number[]} out
 * @param {number[]} dir  Forward direction [x,y,z].
 * @param {number[]} [up] Up vector [x,y,z].
 * @returns {number[]} out
 */
const qFromLookDir = (out, dir, up) => {
  let fx=dir[0],fy=dir[1],fz=dir[2];
  const fl=Math.sqrt(fx*fx+fy*fy+fz*fz)||1;
  fx/=fl; fy/=fl; fz/=fl;
  let ux=up?up[0]:0, uy=up?up[1]:1, uz=up?up[2]:0;
  let rx=uy*fz-uz*fy, ry=uz*fx-ux*fz, rz=ux*fy-uy*fx;
  const rl=Math.sqrt(rx*rx+ry*ry+rz*rz)||1;
  rx/=rl; ry/=rl; rz/=rl;
  ux=fy*rz-fz*ry; uy=fz*rx-fx*rz; uz=fx*ry-fy*rx;
  return qFromRotMat3x3(out, rx,ry,rz, ux,uy,uz, -fx,-fy,-fz);
};

/**
 * Build a quaternion from a 3×3 rotation matrix (9 row-major scalars).
 * @returns {number[]} out (normalised)
 */
const qFromRotMat3x3 = (out, m00,m01,m02, m10,m11,m12, m20,m21,m22) => {
  const tr = m00+m11+m22;
  if (tr > 0) {
    const s=0.5/Math.sqrt(tr+1);
    out[3]=0.25/s; out[0]=(m21-m12)*s; out[1]=(m02-m20)*s; out[2]=(m10-m01)*s;
  } else if (m00>m11 && m00>m22) {
    const s=2*Math.sqrt(1+m00-m11-m22);
    out[3]=(m21-m12)/s; out[0]=0.25*s; out[1]=(m01+m10)/s; out[2]=(m02+m20)/s;
  } else if (m11>m22) {
    const s=2*Math.sqrt(1+m11-m00-m22);
    out[3]=(m02-m20)/s; out[0]=(m01+m10)/s; out[1]=0.25*s; out[2]=(m12+m21)/s;
  } else {
    const s=2*Math.sqrt(1+m22-m00-m11);
    out[3]=(m10-m01)/s; out[0]=(m02+m20)/s; out[1]=(m12+m21)/s; out[2]=0.25*s;
  }
  return qNormalize(out);
};

/**
 * Extract a unit quaternion from the upper-left 3×3 of a column-major mat4.
 * @param {number[]} out
 * @param {Float32Array|number[]} m  Column-major mat4.
 * @returns {number[]} out
 */
const qFromMat4 = (out, m) =>
  qFromRotMat3x3(out, m[0],m[4],m[8], m[1],m[5],m[9], m[2],m[6],m[10]);

/**
 * Write a quaternion into the rotation block of a column-major mat4.
 * Translation and perspective rows/cols are set to identity values.
 * @param {Float32Array|number[]} out  16-element array.
 * @param {number[]} q  [x,y,z,w].
 * @returns {Float32Array|number[]} out
 */
const qToMat4 = (out, q) => {
  const x=q[0],y=q[1],z=q[2],w=q[3];
  const x2=x+x,y2=y+y,z2=z+z;
  const xx=x*x2,xy=x*y2,xz=x*z2,yy=y*y2,yz=y*z2,zz=z*z2,wx=w*x2,wy=w*y2,wz=w*z2;
  out[0]=1-(yy+zz); out[1]=xy+wz;     out[2]=xz-wy;     out[3]=0;
  out[4]=xy-wz;     out[5]=1-(xx+zz); out[6]=yz+wx;     out[7]=0;
  out[8]=xz+wy;     out[9]=yz-wx;     out[10]=1-(xx+yy); out[11]=0;
  out[12]=0;        out[13]=0;        out[14]=0;          out[15]=1;
  return out;
};

/**
 * Decompose a unit quaternion into { axis:[x,y,z], angle } (radians).
 * @param {number[]} q  [x,y,z,w].
 * @param {Object}  [out]
 * @returns {{ axis: number[], angle: number }}
 */
const quatToAxisAngle = (q, out) => {
  out = out || {};
  const x=q[0],y=q[1],z=q[2],w=q[3];
  const sinHalf = Math.sqrt(x*x+y*y+z*z);
  if (sinHalf < 1e-8) { out.axis=[0,1,0]; out.angle=0; return out; }
  out.angle = 2*Math.atan2(sinHalf, w);
  out.axis  = [x/sinHalf, y/sinHalf, z/sinHalf];
  return out;
};

// =========================================================================
// S2  Spline / vector helpers
// =========================================================================

function _dist3(a, b) {
  const dx=a[0]-b[0], dy=a[1]-b[1], dz=a[2]-b[2];
  return Math.sqrt(dx*dx+dy*dy+dz*dz);
}

/**
 * Cubic Hermite interpolation between p0 and p1 with explicit tangents.
 * Catmull-Rom is a special case where m0/m1 are auto-computed from neighbors.
 * @param {number[]} out  3-element result.
 * @param {number[]} p0   Segment start.
 * @param {number[]} m0   Outgoing tangent at p0 (world-space, dp/dt scaled to segment).
 * @param {number[]} p1   Segment end.
 * @param {number[]} m1   Incoming tangent at p1 (world-space, dp/dt scaled to segment).
 * @param {number}   t    Blend [0, 1].
 * @returns {number[]} out
 */
const hermiteVec3 = (out, p0, m0, p1, m1, t) => {
  const t2=t*t, t3=t2*t;
  const h00=2*t3-3*t2+1, h10=t3-2*t2+t, h01=-2*t3+3*t2, h11=t3-t2;
  out[0]=h00*p0[0]+h10*m0[0]+h01*p1[0]+h11*m1[0];
  out[1]=h00*p0[1]+h10*m0[1]+h01*p1[1]+h11*m1[1];
  out[2]=h00*p0[2]+h10*m0[2]+h01*p1[2]+h11*m1[2];
  return out;
};

// Centripetal CR outgoing tangent at p1 for segment p1→p2, scaled by dt1.
const _crTanOut = (out, p0, p1, p2, p3) => {
  const dt0=Math.pow(_dist3(p0,p1),0.5)||1, dt1=Math.pow(_dist3(p1,p2),0.5)||1; Math.pow(_dist3(p2,p3),0.5)||1;
  for (let i=0;i<3;i++) out[i]=((p1[i]-p0[i])/dt0-(p2[i]-p0[i])/(dt0+dt1)+(p2[i]-p1[i])/dt1)*dt1;
  return out;
};

// Centripetal CR incoming tangent at p2 for segment p1→p2, scaled by dt1.
const _crTanIn = (out, p0, p1, p2, p3) => {
  Math.pow(_dist3(p0,p1),0.5)||1; const dt1=Math.pow(_dist3(p1,p2),0.5)||1, dt2=Math.pow(_dist3(p2,p3),0.5)||1;
  for (let i=0;i<3;i++) out[i]=((p2[i]-p1[i])/dt1-(p3[i]-p1[i])/(dt1+dt2)+(p3[i]-p2[i])/dt2)*dt1;
  return out;
};

// Module-level scratch — shared by eval() across all track instances (non-reentrant hot path).
const _m0=[0,0,0], _m1=[0,0,0];

/**
 * Linear interpolation between two vec3s.
 * @param {number[]} out
 * @param {number[]} a
 * @param {number[]} b
 * @param {number}   t  Blend [0, 1].
 * @returns {number[]} out
 */
const lerpVec3 = (out, a, b, t) => {
  out[0]=a[0]+t*(b[0]-a[0]);
  out[1]=a[1]+t*(b[1]-a[1]);
  out[2]=a[2]+t*(b[2]-a[2]);
  return out;
};

// =========================================================================
// S3  Transform <-> Mat4
// =========================================================================

/**
 * Write a TRS transform into a column-major mat4.
 * @param {Float32Array|number[]} out  16-element column-major mat4.
 * @param {{ pos:number[], rot:number[], scl:number[] }} xform
 * @returns {Float32Array|number[]} out
 */
const transformToMat4 = (out, xform) => {
  qToMat4(out, xform.rot);
  const sx=xform.scl[0], sy=xform.scl[1], sz=xform.scl[2];
  out[0]*=sx; out[1]*=sx; out[2]*=sx;
  out[4]*=sy; out[5]*=sy; out[6]*=sy;
  out[8]*=sz; out[9]*=sz; out[10]*=sz;
  out[12]=xform.pos[0]; out[13]=xform.pos[1]; out[14]=xform.pos[2];
  return out;
};

/**
 * Decompose a column-major mat4 into a TRS transform.
 * Assumes no shear. Scale extracted from column lengths.
 * @param {{ pos:number[], rot:number[], scl:number[] }} out
 * @param {Float32Array|number[]} m  Column-major mat4.
 * @returns {{ pos:number[], rot:number[], scl:number[] }} out
 */
const mat4ToTransform = (out, m) => {
  out.pos[0]=m[12]; out.pos[1]=m[13]; out.pos[2]=m[14];
  const sx=Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2]);
  const sy=Math.sqrt(m[4]*m[4]+m[5]*m[5]+m[6]*m[6]);
  const sz=Math.sqrt(m[8]*m[8]+m[9]*m[9]+m[10]*m[10]);
  out.scl[0]=sx; out.scl[1]=sy; out.scl[2]=sz;
  qFromRotMat3x3(out.rot,
    m[0]/sx,m[4]/sy,m[8]/sz,
    m[1]/sx,m[5]/sy,m[9]/sz,
    m[2]/sx,m[6]/sy,m[10]/sz);
  return out;
};

// =========================================================================
// S4a  Spec parser — PoseTrack
// =========================================================================

const _isNum   = (x) => typeof x === 'number' && Number.isFinite(x);
const _clamp01 = (x) => x < 0 ? 0 : (x > 1 ? 1 : x);
const _clampS  = (x, lo, hi) => x < lo ? lo : (x > hi ? hi : x);

function _parseVec3(v) {
  if (!v) return null;
  if (ArrayBuffer.isView(v) && v.length >= 3) return [v[0], v[1], v[2]];
  if (Array.isArray(v) && v.length >= 3 && v.every(n => typeof n === 'number')) return [v[0], v[1], v[2]];
  if (typeof v === 'object' && 'x' in v) return [v.x || 0, v.y || 0, v.z || 0];
  return null;
}

// Euler: unit axis vectors and the six valid intrinsic orderings.
const _EULER_AXES   = { X:[1,0,0], Y:[0,1,0], Z:[0,0,1] };
const _EULER_ORDERS = new Set(['XYZ','XZY','YXZ','YZX','ZXY','ZYX']);

/**
 * Parse any rotation representation into a unit quaternion [x,y,z,w].
 *
 * Accepted forms:
 *
 *   [x,y,z,w]
 *     Raw quaternion array.
 *
 *   { axis:[x,y,z], angle }
 *     Axis-angle.  Axis need not be unit.
 *
 *   { dir:[x,y,z], up?:[x,y,z] }
 *     Object orientation — forward direction (−Z) with optional up hint.
 *
 *   { eMatrix: mat4 }
 *     Extract rotation block from an eye (eye→world) matrix.
 *     Column-major Float32Array(16), plain Array, or { mat4 } wrapper.
 *
 *   { mat3: mat3 }
 *     Column-major 3×3 rotation matrix — Float32Array(9) or plain Array.
 *
 *   { euler:[rx,ry,rz], order?:'YXZ' }
 *     Intrinsic Euler angles (radians).  Angles are indexed by order position:
 *     e[0] rotates around order[0] axis, e[1] around order[1], e[2] around order[2].
 *     Supported orders: YXZ (default), XYZ, ZYX, ZXY, XZY, YZX.
 *     Note: intrinsic ABC = extrinsic CBA with the same angles — to use
 *     extrinsic order ABC, reverse the string and use intrinsic CBA.
 *
 *   { from:[x,y,z], to:[x,y,z] }
 *     Shortest-arc rotation from one direction onto another.
 *     Both vectors are normalised internally.
 *     Antiparallel input: 180° rotation around a perpendicular axis.
 *
 * @param {*} v
 * @returns {number[]|null}  [x,y,z,w] or null if unparseable.
 */
function _parseQuat(v) {
  if (!v) return null;

  // raw [x,y,z,w] — plain array or typed array
  if ((Array.isArray(v) || ArrayBuffer.isView(v)) && v.length === 4) return [v[0], v[1], v[2], v[3]];

  // { axis, angle }
  if (v.axis && typeof v.angle === 'number') {
    const a = Array.isArray(v.axis) ? v.axis : [v.axis.x||0, v.axis.y||0, v.axis.z||0];
    return qFromAxisAngle([0,0,0,1], a[0],a[1],a[2], v.angle);
  }

  // { dir, up? }
  if (v.dir) {
    const d = Array.isArray(v.dir) ? v.dir : [v.dir.x||0, v.dir.y||0, v.dir.z||0];
    const u = v.up ? (Array.isArray(v.up) ? v.up : [v.up.x||0, v.up.y||0, v.up.z||0]) : null;
    return qFromLookDir([0,0,0,1], d, u);
  }

  // { eMatrix } — rotation block from eye (eye→world) matrix, col-major mat4
  if (v.eMatrix != null) {
    const m = (ArrayBuffer.isView(v.eMatrix) || Array.isArray(v.eMatrix))
      ? v.eMatrix : (v.eMatrix.mat4 ?? null);
    if (m && m.length >= 16) return qFromMat4([0,0,0,1], m);
  }

  // { mat3 } — column-major 3×3 rotation matrix
  // col0=[m0,m1,m2], col1=[m3,m4,m5], col2=[m6,m7,m8]
  // row-major for qFromRotMat3x3: row0=[m0,m3,m6], row1=[m1,m4,m7], row2=[m2,m5,m8]
  if (v.mat3 != null) {
    const m = v.mat3;
    if ((ArrayBuffer.isView(m) || Array.isArray(m)) && m.length >= 9)
      return qFromRotMat3x3([0,0,0,1], m[0],m[3],m[6], m[1],m[4],m[7], m[2],m[5],m[8]);
  }

  // { euler, order? } — intrinsic Euler angles (radians), default order YXZ
  if (v.euler != null) {
    const e = v.euler;
    if (!Array.isArray(e) || e.length < 3) return null;
    const order = (typeof v.order === 'string' && _EULER_ORDERS.has(v.order))
      ? v.order : 'YXZ';
    const q = [0,0,0,1];
    const s = [0,0,0,1]; // scratch — reused each step
    for (let i = 0; i < 3; i++) {
      const ax = _EULER_AXES[order[i]];
      qMul(q, q, qFromAxisAngle(s, ax[0],ax[1],ax[2], e[i]));
    }
    return q;
  }

  // { from, to } — shortest-arc rotation from one direction onto another
  if (v.from != null && v.to != null) {
    const f = Array.isArray(v.from) ? v.from : [v.from.x||0, v.from.y||0, v.from.z||0];
    const t = Array.isArray(v.to)   ? v.to   : [v.to.x||0,   v.to.y||0,   v.to.z||0];
    const fl = Math.sqrt(f[0]*f[0]+f[1]*f[1]+f[2]*f[2]) || 1;
    const tl = Math.sqrt(t[0]*t[0]+t[1]*t[1]+t[2]*t[2]) || 1;
    const fx=f[0]/fl, fy=f[1]/fl, fz=f[2]/fl;
    const tx=t[0]/tl, ty=t[1]/tl, tz=t[2]/tl;
    const dot = fx*tx + fy*ty + fz*tz;
    // parallel — identity
    if (dot >= 1 - 1e-8) return [0,0,0,1];
    // antiparallel — 180° around any perpendicular axis
    if (dot <= -1 + 1e-8) {
      // cross(from, X=[1,0,0]) = [0, fz, -fy]
      let px=0, py=fz, pz=-fy;
      let pl = Math.sqrt(px*px+py*py+pz*pz);
      if (pl < 1e-8) {
        // from ≈ ±X; try cross(from, Z=[0,0,1]) = [fy, -fx, 0]
        px=fy; py=-fx; pz=0;
        pl = Math.sqrt(px*px+py*py+pz*pz);
      }
      if (pl < 1e-8) return [0,0,0,1];
      return qFromAxisAngle([0,0,0,1], px/pl,py/pl,pz/pl, Math.PI);
    }
    // general case — axis = normalize(cross(from, to))
    let ax=fy*tz-fz*ty, ay=fz*tx-fx*tz, az=fx*ty-fy*tx;
    const al = Math.sqrt(ax*ax+ay*ay+az*az) || 1;
    return qFromAxisAngle([0,0,0,1], ax/al,ay/al,az/al,
      Math.acos(Math.max(-1, Math.min(1, dot))));
  }

  return null;
}

/**
 * Parse a PoseTrack keyframe spec.
 *
 * Accepted forms:
 *
 *   { mMatrix }
 *     Decompose a column-major mat4 into TRS via mat4ToTransform.
 *     Float32Array(16), plain Array, or { mat4 } wrapper.
 *     pos from col3, scl from column lengths, rot from normalised rotation block.
 *
 *   { pos?, rot?, scl?, tanIn?, tanOut? }
 *     Explicit TRS.  pos and scl are vec3, rot accepts any form from _parseQuat.
 *     All fields are optional — missing pos/scl default to [0,0,0] / [1,1,1],
 *     missing rot defaults to identity.
 *     tanIn/tanOut are optional vec3 tangents for Hermite interpolation.
 *     When absent, centripetal Catmull-Rom tangents are auto-computed at eval time.
 *
 * @param {Object} spec
 * @returns {{ pos:number[], rot:number[], scl:number[], tanIn:number[]|null, tanOut:number[]|null }|null}
 */
function _parseSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;

  // { mMatrix } — full TRS decomposition from model matrix
  if (spec.mMatrix != null) {
    const m = (ArrayBuffer.isView(spec.mMatrix) || Array.isArray(spec.mMatrix))
      ? spec.mMatrix : (spec.mMatrix.mat4 ?? null);
    if (!m || m.length < 16) return null;
    const kf = mat4ToTransform({ pos:[0,0,0], rot:[0,0,0,1], scl:[1,1,1] }, m);
    kf.tanIn = null; kf.tanOut = null;
    return kf;
  }

  const pos    = _parseVec3(spec.pos)    || [0,0,0];
  const rot    = _parseQuat(spec.rot)    || [0,0,0,1];
  const scl    = _parseVec3(spec.scl)    || [1,1,1];
  const tanIn  = _parseVec3(spec.tanIn)  || null;
  const tanOut = _parseVec3(spec.tanOut) || null;
  return { pos, rot, scl, tanIn, tanOut };
}

function _sameTransform(a, b) {
  for (let i=0;i<3;i++) if (a.pos[i]!==b.pos[i]||a.scl[i]!==b.scl[i]) return false;
  for (let i=0;i<4;i++) if (a.rot[i]!==b.rot[i]) return false;
  return true;
}

// =========================================================================
// S4b  Spec parser — CameraTrack
// =========================================================================

/**
 * Parse a camera keyframe spec into internal { eye, center, up } form.
 *
 * Accepted forms:
 *
 *   { eye, center?, up?, fov?, halfHeight?,
 *     eyeTanIn?, eyeTanOut?, centerTanIn?, centerTanOut? }
 *     Explicit lookat.  center defaults to [0,0,0], up defaults to [0,1,0].
 *     Both are normalised/stored as-is.  eye must be a vec3.
 *     eyeTanIn/Out and centerTanIn/Out are optional vec3 tangents for Hermite.
 *     When absent, centripetal Catmull-Rom tangents are auto-computed at eval time.
 *
 *   { vMatrix: mat4 }
 *     Column-major view matrix (world→eye).
 *     eye reconstructed via -R^T·t; center = eye + forward·1; up = [0,1,0].
 *     The matrix's up_ortho (col1) is intentionally NOT used as up —
 *     passing it to cam.camera() shifts orbitControl's orbit reference.
 *     Float32Array(16), plain Array, or { mat4 } wrapper.
 *
 *   { eMatrix: mat4 }
 *     Column-major eye matrix (eye→world, i.e. inverse view).
 *     eye read directly from col3; center = eye + forward·1; up = [0,1,0].
 *     Simpler extraction than vMatrix; prefer this form when eMatrix is available.
 *     Float32Array(16), plain Array, or { mat4 } wrapper.
 *
 * @param {Object} spec
 * @returns {{ eye:number[], center:number[], up:number[],
 *             fov:number|null, halfHeight:number|null,
 *             eyeTanIn:number[]|null, eyeTanOut:number[]|null,
 *             centerTanIn:number[]|null, centerTanOut:number[]|null }|null}
 */
function _parseCameraSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;

  // { vMatrix } — view matrix (world→eye); reconstruct eye via -R^T·t
  if (spec.vMatrix != null) {
    const m = (ArrayBuffer.isView(spec.vMatrix) || Array.isArray(spec.vMatrix))
      ? spec.vMatrix : (spec.vMatrix.mat4 ?? null);
    if (!m || m.length < 16) return null;
    const ex = -(m[0]*m[12] + m[4]*m[13] + m[8]*m[14]);
    const ey = -(m[1]*m[12] + m[5]*m[13] + m[9]*m[14]);
    const ez = -(m[2]*m[12] + m[6]*m[13] + m[10]*m[14]);
    const fx=-m[8], fy=-m[9], fz=-m[10];
    const fl=Math.sqrt(fx*fx+fy*fy+fz*fz)||1;
    return { eye:[ex,ey,ez], center:[ex+fx/fl,ey+fy/fl,ez+fz/fl], up:[0,1,0],
             fov:null, halfHeight:null,
             eyeTanIn:null, eyeTanOut:null, centerTanIn:null, centerTanOut:null };
  }

  // { eMatrix } — eye matrix (eye→world); eye = col3, forward = -col2
  if (spec.eMatrix != null) {
    const m = (ArrayBuffer.isView(spec.eMatrix) || Array.isArray(spec.eMatrix))
      ? spec.eMatrix : (spec.eMatrix.mat4 ?? null);
    if (!m || m.length < 16) return null;
    const ex=m[12], ey=m[13], ez=m[14];
    const fx=-m[8], fy=-m[9], fz=-m[10];
    const fl=Math.sqrt(fx*fx+fy*fy+fz*fz)||1;
    return { eye:[ex,ey,ez], center:[ex+fx/fl,ey+fy/fl,ez+fz/fl], up:[0,1,0],
             fov:null, halfHeight:null,
             eyeTanIn:null, eyeTanOut:null, centerTanIn:null, centerTanOut:null };
  }

  // { eye, center?, up? } — explicit lookat (eye is a vec3, not a mat4)
  const eye    = _parseVec3(spec.eye);
  if (!eye) return null;
  const center = _parseVec3(spec.center) || [0,0,0];
  const upRaw  = spec.up ? _parseVec3(spec.up) : null;
  const up     = upRaw || [0,1,0];
  const ul     = Math.sqrt(up[0]*up[0]+up[1]*up[1]+up[2]*up[2]) || 1;
  return {
    eye, center,
    up: [up[0]/ul, up[1]/ul, up[2]/ul],
    fov:         typeof spec.fov        === 'number' ? spec.fov        : null,
    halfHeight:  typeof spec.halfHeight === 'number' ? spec.halfHeight : null,
    eyeTanIn:    _parseVec3(spec.eyeTanIn)    || null,
    eyeTanOut:   _parseVec3(spec.eyeTanOut)   || null,
    centerTanIn: _parseVec3(spec.centerTanIn) || null,
    centerTanOut:_parseVec3(spec.centerTanOut)|| null,
  };
}

function _sameCameraKeyframe(a, b) {
  for (let i=0;i<3;i++) {
    if (a.eye[i]!==b.eye[i]) return false;
    if (a.center[i]!==b.center[i]) return false;
    if (a.up[i]!==b.up[i]) return false;
  }
  if (a.fov !== b.fov) return false;
  if (a.halfHeight !== b.halfHeight) return false;
  return true;
}

// =========================================================================
// S5  Track — unexported base class (transport machinery only)
// =========================================================================

class Track {
  constructor() {
    /** @type {Array} Keyframe array — shape depends on subclass. */
    this.keyframes = [];
    /** Whether playback is active. @type {boolean} */
    this.playing   = false;
    /** Loop at boundaries. @type {boolean} */
    this.loop      = false;
    /** Ping-pong bounce (takes precedence over loop). @type {boolean} */
    this.bounce  = false;
    /** Frames per segment (≥1). @type {number} */
    this.duration  = 30;
    /** Current segment index. @type {number} */
    this.seg       = 0;
    /** Frame offset within segment (can be fractional). @type {number} */
    this.f         = 0;

    // Internal rate — never directly starts/stops playback
    this._rate = 1;
    // Internal bounce direction: +1 forward, -1 backward.
    // Flipped by tick() at boundaries. Never exposed publicly.
    // rate always holds the user-set value — only _dir changes.
    this._dir  = 1;

    // User-space hooks
    /** @type {Function|null} */ this.onPlay = null;
    /** @type {Function|null} */ this.onEnd  = null;
    /** @type {Function|null} */ this.onStop = null;

    // Lib-space hooks (set by host layer, e.g. p5 bridge)
    /** @type {Function|null} */ this._onActivate   = null;
    /** @type {Function|null} */ this._onDeactivate = null;
    // Lib-space event mirrors — set by UI layer (trackUI), never touched by user code
    /** @type {Function|null} */ this._onPlay = null;
    /** @type {Function|null} */ this._onEnd  = null;
    /** @type {Function|null} */ this._onStop = null;
  }

  /** Playback rate. Assigning never starts/stops playback. @type {number} */
  get rate()  { return this._rate; }
  set rate(v) { this._rate = (_isNum(v)) ? v : 1; }

  /** Number of interpolatable segments (keyframes.length − 1, min 0). @type {number} */
  get segments() { return Math.max(0, this.keyframes.length - 1); }

  /**
   * Start or update playback.
   * @param {number|Object} [rateOrOpts]  Numeric rate or options object:
   *   { rate, duration, loop, bounce, onPlay, onEnd, onStop }
   * @returns {Track} this
   */
  play(rateOrOpts) {
    if (this.keyframes.length === 0) return this;

    // One keyframe: snap cursor, no animation
    if (this.keyframes.length === 1) {
      this.seg = 0; this.f = 0;
      return this;
    }

    if (typeof rateOrOpts === 'number' && Number.isFinite(rateOrOpts)) {
      this._rate = rateOrOpts;
    } else if (rateOrOpts && typeof rateOrOpts === 'object') {
      const o = rateOrOpts;
      if (_isNum(o.duration))             this.duration  = Math.max(1, o.duration | 0);
      if ('loop'     in o) { this.loop     = !!o.loop;     if (!this.loop)     this.bounce = false; }
      if ('bounce' in o) { this.bounce = !!o.bounce; if (this.bounce)  this.loop     = true;  }
      if (typeof o.onPlay === 'function') this.onPlay   = o.onPlay;
      if (typeof o.onEnd  === 'function') this.onEnd    = o.onEnd;
      if (typeof o.onStop === 'function') this.onStop   = o.onStop;
      if (_isNum(o.rate))                 this._rate    = o.rate;
    }

    const nSeg = this.segments, dur = Math.max(1, this.duration | 0);
    if (this.seg < 0)     this.seg = 0;
    if (this.seg >= nSeg) this.seg = nSeg - 1;
    if (this.f   < 0)     this.f   = 0;
    if (this.f   > dur)   this.f   = dur;

    const wasPlaying = this.playing;
    this.playing = true;
    if (!wasPlaying) {
      if (typeof this.onPlay === 'function') { try { this.onPlay(this); } catch (_) {} }
      this._onPlay?.();
      this._onActivate?.();
    }
    return this;
  }

  /**
   * Stop playback.
   * @param {boolean} [rewind=false]  Seek to origin after stopping.
   * @returns {Track} this
   */
  stop(rewind) {
    const wasPlaying = this.playing;
    this.playing = false;
    if (wasPlaying) {
      if (typeof this.onStop === 'function') { try { this.onStop(this); } catch (_) {} }
      this._onStop?.();
      this._onDeactivate?.();
      if (rewind && this.keyframes.length > 1) this.seek(this._rate * this._dir < 0 ? 1 : 0);
    }
    return this;
  }

  /**
   * Clear all keyframes and stop.
   * @returns {Track} this
   */
  reset() {
    const wasPlaying = this.playing;
    this.playing = false;
    if (wasPlaying) {
      if (typeof this.onStop === 'function') { try { this.onStop(this); } catch (_) {} }
      this._onStop?.();
      this._onDeactivate?.();
    }
    this.keyframes.length = 0;
    this.seg = 0; this.f = 0; this._dir = 1;
    return this;
  }

  /**
   * Remove the keyframe at index. Adjusts cursor if needed.
   * @param {number} index
   * @returns {boolean}
   */
  remove(index) {
    if (!_isNum(index)) return false;
    const i = index | 0;
    if (i < 0 || i >= this.keyframes.length) return false;
    this.keyframes.splice(i, 1);
    const nSeg = this.segments;
    if (nSeg === 0) { this.seg = 0; this.f = 0; }
    else if (this.seg >= nSeg) { this.seg = nSeg - 1; }
    return true;
  }

  /**
   * Seek to a normalised position [0,1] across the full path.
   * @param {number} t           Normalised time [0, 1].
   * @param {number} [segIndex]  Optional segment override.
   * @returns {Track} this
   */
  seek(t, segIndex) {
    const nSeg = this.segments;
    if (nSeg === 0) { this.seg = 0; this.f = 0; return this; }
    const dur = Math.max(1, this.duration | 0);
    if (_isNum(segIndex)) {
      this.seg = _clampS(segIndex | 0, 0, nSeg - 1);
      this.f   = _clamp01(t) * dur;
    } else {
      this._setCursorFromScalar(_clamp01(t) * nSeg * dur);
    }
    return this;
  }

  /**
   * Normalised playback position [0,1].
   * @returns {number}
   */
  time() {
    const nSeg = this.segments;
    if (nSeg === 0) return 0;
    const dur = Math.max(1, this.duration | 0);
    return _clamp01((this.seg * dur + this.f) / (nSeg * dur));
  }

  /**
   * Snapshot of transport state.
   * @returns {Object}
   */
  info() {
    return {
      keyframes: this.keyframes.length,
      segments:  this.segments,
      seg:       this.seg,
      f:         this.f,
      playing:   this.playing,
      loop:      this.loop,
      bounce:  this.bounce,
      rate:      this._rate,
      duration:  this.duration,
      time:      this.segments > 0 ? this.time() : 0
    };
  }

  /**
   * Advance cursor by rate frames.
   * Returns true while playing, false when stopping.
   * @returns {boolean}
   */
  tick() {
    if (!this.playing) return false;
    const nSeg = this.segments;
    if (nSeg === 0) {
      this.playing = false; this._onDeactivate?.(); return false;
    }
    if (this._rate === 0) return true;

    const dur   = Math.max(1, this.duration | 0);
    const total = nSeg * dur;
    const s     = _clampS(this.seg * dur + this.f, 0, total);
    const next  = s + this._rate * this._dir;

    if (this.bounce) {
      let pos = next, flips = 0;
      while (pos < 0 || pos > total) {
        if (pos < 0) { pos = -pos; flips++; }
        else         { pos = 2 * total - pos; flips++; }
      }
      if (flips & 1) this._dir = -this._dir;
      this._setCursorFromScalar(pos);
      return true;
    }

    if (this.loop) {
      this._setCursorFromScalar(((next % total) + total) % total);
      return true;
    }

    if (next <= 0) {
      this._setCursorFromScalar(0);
      this.playing = false;
      if (typeof this.onEnd === 'function') { try { this.onEnd(this); } catch (_) {} }
      this._onEnd?.();
      this._onDeactivate?.();
      return false;
    }
    if (next >= total) {
      this._setCursorFromScalar(total);
      this.playing = false;
      if (typeof this.onEnd === 'function') { try { this.onEnd(this); } catch (_) {} }
      this._onEnd?.();
      this._onDeactivate?.();
      return false;
    }

    this._setCursorFromScalar(next);
    return true;
  }

  /** @private */
  _setCursorFromScalar(s) {
    const dur  = Math.max(1, this.duration | 0);
    const nSeg = this.segments;
    this.seg = Math.floor(s / dur);
    this.f   = s - this.seg * dur;
    if (this.seg >= nSeg) { this.seg = nSeg - 1; this.f = dur; }
    if (this.seg < 0)     { this.seg = 0;         this.f = 0;   }
  }
}

// =========================================================================
// S6  PoseTrack
// =========================================================================

/**
 * Renderer-agnostic TRS keyframe track.
 *
 * Keyframe shape: { pos:[x,y,z], rot:[x,y,z,w], scl:[x,y,z],
 *                   tanIn?:[x,y,z], tanOut?:[x,y,z] }
 *
 * tanIn  — incoming position tangent at this keyframe (Hermite mode).
 * tanOut — outgoing position tangent at this keyframe (Hermite mode).
 * When only one is supplied, the other mirrors it.
 * When neither is supplied, centripetal Catmull-Rom tangents are auto-computed.
 *
 * add() accepts individual specs or a bulk array of specs:
 *
 *   { mMatrix }                           — full TRS from model matrix
 *   { pos?, rot?, scl?, tanIn?, tanOut? } — direct TRS; all fields optional
 *   { pos?, rot: [x,y,z,w] }             — explicit quaternion
 *   { pos?, rot: { axis, angle } }        — axis-angle
 *   { pos?, rot: { dir, up? } }           — look direction
 *   { pos?, rot: { eMatrix: mat4 } }      — rotation from eye matrix
 *   { pos?, rot: { mat3 } }               — column-major 3×3 rotation matrix
 *   { pos?, rot: { euler, order? } }      — intrinsic Euler angles (default YXZ)
 *   { pos?, rot: { from, to } }           — shortest-arc between two directions
 *   [ spec, spec, ... ]                   — bulk
 *
 * Missing fields default to: pos → [0,0,0], rot → [0,0,0,1], scl → [1,1,1].
 *
 * eval() writes { pos, rot, scl }:
 *   pos — Hermite (tanIn/tanOut per keyframe; auto-CR when absent) or linear or step
 *   rot — slerp (rotInterp='slerp') or nlerp or step
 *   scl — lerp
 *
 * @example
 * const track = new PoseTrack()
 * track.add({ pos:[0,0,0] })
 * track.add({ pos:[100,0,0], tanOut:[0,50,0] })   // leave heading +Y
 * track.add({ pos:[200,0,0] })
 * track.play({ loop: true })
 * // per frame:
 * track.tick()
 * const out = { pos:[0,0,0], rot:[0,0,0,1], scl:[1,1,1] }
 * track.eval(out)
 */
class PoseTrack extends Track {
  constructor() {
    super();
    /**
     * Position interpolation mode.
     * - 'hermite' — cubic Hermite; uses tanIn/tanOut per keyframe when present,
     *               auto-computes centripetal Catmull-Rom tangents when absent (default)
     * - 'linear'  — lerp
     * - 'step'    — snap to k0 value; useful for discrete state changes
     * @type {'hermite'|'linear'|'step'}
     */
    this.posInterp = 'hermite';
    /**
     * Rotation interpolation mode.
     * - 'slerp'  — constant angular velocity (default)
     * - 'nlerp'  — normalised lerp; cheaper, slightly non-constant speed
     * - 'step'   — snap to k0 quaternion; useful for discrete state changes
     * @type {'slerp'|'nlerp'|'step'}
     */
    this.rotInterp = 'slerp';
    // Scratch for toMatrix() — avoids hot-path allocations
    this._pos = [0,0,0];
    this._rot = [0,0,0,1];
    this._scl = [1,1,1];
  }

  /**
   * Append one or more keyframes. Adjacent duplicates are skipped by default.
   * @param {Object|Object[]} spec
   * @param {{ deduplicate?: boolean }} [opts]
   */
  add(spec, opts) {
    if (Array.isArray(spec)) {
      for (const s of spec) this.add(s, opts);
      return;
    }
    const kf = _parseSpec(spec);
    if (!kf) return;
    const dedup = !opts || opts.deduplicate !== false;
    if (dedup && this.keyframes.length > 0) {
      if (_sameTransform(this.keyframes[this.keyframes.length - 1], kf)) return;
    }
    this.keyframes.push(kf);
  }

  /**
   * Replace (or append at end) the keyframe at index.
   * @param {number} index
   * @param {Object} spec
   * @returns {boolean}
   */
  set(index, spec) {
    if (!_isNum(index)) return false;
    const i = index | 0, kf = _parseSpec(spec);
    if (!kf || i < 0 || i > this.keyframes.length) return false;
    if (i === this.keyframes.length) this.keyframes.push(kf);
    else this.keyframes[i] = kf;
    return true;
  }

  /**
   * Evaluate interpolated TRS pose at current cursor.
   * @param {{ pos:number[], rot:number[], scl:number[] }} [out]
   * @returns {{ pos:number[], rot:number[], scl:number[] }} out
   */
  eval(out) {
    out = out || { pos:[0,0,0], rot:[0,0,0,1], scl:[1,1,1] };
    const n = this.keyframes.length;
    if (n === 0) return out;

    if (n === 1) {
      const k = this.keyframes[0];
      out.pos[0]=k.pos[0]; out.pos[1]=k.pos[1]; out.pos[2]=k.pos[2];
      out.rot[0]=k.rot[0]; out.rot[1]=k.rot[1]; out.rot[2]=k.rot[2]; out.rot[3]=k.rot[3];
      out.scl[0]=k.scl[0]; out.scl[1]=k.scl[1]; out.scl[2]=k.scl[2];
      return out;
    }

    const nSeg = n - 1;
    const dur  = Math.max(1, this.duration | 0);
    const seg  = _clampS(this.seg, 0, nSeg - 1);
    const t    = _clamp01(this.f / dur);
    const k0   = this.keyframes[seg];
    const k1   = this.keyframes[seg + 1];

    // pos — Hermite (auto-CR tangents when none stored), linear, or step
    if (this.posInterp === 'step') {
      out.pos[0]=k0.pos[0]; out.pos[1]=k0.pos[1]; out.pos[2]=k0.pos[2];
    } else if (this.posInterp === 'linear') {
      lerpVec3(out.pos, k0.pos, k1.pos, t);
    } else {
      const p0 = seg > 0      ? this.keyframes[seg - 1].pos : k0.pos;
      const p3 = seg + 2 < n ? this.keyframes[seg + 2].pos  : k1.pos;
      // tanOut on k0: use stored, else symmetric from tanIn, else auto-CR
      const m0 = k0.tanOut != null ? k0.tanOut
               : k0.tanIn  != null ? k0.tanIn
               : _crTanOut(_m0, p0, k0.pos, k1.pos, p3);
      // tanIn on k1: use stored, else symmetric from tanOut, else auto-CR
      const m1 = k1.tanIn  != null ? k1.tanIn
               : k1.tanOut != null ? k1.tanOut
               : _crTanIn(_m1, p0, k0.pos, k1.pos, p3);
      hermiteVec3(out.pos, k0.pos, m0, k1.pos, m1, t);
    }

    // rot — step, slerp, or nlerp
    if (this.rotInterp === 'step') {
      out.rot[0]=k0.rot[0]; out.rot[1]=k0.rot[1]; out.rot[2]=k0.rot[2]; out.rot[3]=k0.rot[3];
    } else if (this.rotInterp === 'nlerp') {
      qNlerp(out.rot, k0.rot, k1.rot, t);
    } else {
      qSlerp(out.rot, k0.rot, k1.rot, t);
    }

    // scl — lerp
    lerpVec3(out.scl, k0.scl, k1.scl, t);

    return out;
  }

  /**
   * Evaluate into an existing column-major mat4.
   * @param {Float32Array|number[]} outMat4  16-element array.
   * @returns {Float32Array|number[]} outMat4
   */
  toMatrix(outMat4) {
    const xf = this.eval({ pos: this._pos, rot: this._rot, scl: this._scl });
    return transformToMat4(outMat4, xf);
  }
}

// =========================================================================
// S7  CameraTrack
// =========================================================================

/**
 * Lookat camera keyframe track.
 *
 * Keyframe shape: { eye:[x,y,z], center:[x,y,z], up:[x,y,z],
 *                   fov?:number, halfHeight?:number,
 *                   eyeTanIn?:[x,y,z], eyeTanOut?:[x,y,z],
 *                   centerTanIn?:[x,y,z], centerTanOut?:[x,y,z] }
 *
 * fov        — vertical fov (radians) for perspective cameras; null for ortho.
 * halfHeight — world-unit half-height of ortho frustum; null for perspective.
 * Both are optional and nullable. eval() lerps each only when both adjacent
 * keyframes carry a non-null value for that field.
 *
 * eyeTanIn/Out and centerTanIn/Out are optional vec3 tangents for Hermite
 * interpolation of the eye and center paths respectively.
 * When absent, centripetal Catmull-Rom tangents are auto-computed at eval time.
 *
 * Each field is independently interpolated — eye and center along their
 * own paths, up nlerped on the unit sphere. This correctly handles cameras
 * that always look at a fixed target (center stays at origin throughout)
 * as well as free-fly paths where center moves independently.
 *
 * Missing fields default to: center → [0,0,0], up → [0,1,0].
 *
 * add() accepts individual specs or a bulk array of specs:
 *
 *   { eye, center?, up?, fov?, halfHeight?,
 *     eyeTanIn?, eyeTanOut?, centerTanIn?, centerTanOut? }
 *                         explicit lookat; center defaults to [0,0,0], up to [0,1,0].
 *                         fov and halfHeight are mutually exclusive nullable scalars.
 *   { vMatrix: mat4 }      view matrix (world→eye); eye reconstructed via -R^T·t
 *   { eMatrix: mat4 }      eye matrix (eye→world); eye read from col3 directly
 *   [ spec, spec, ... ]    bulk
 *
 * Note on up for matrix forms:
 *   up is always [0,1,0].  The matrix's col1 (up_ortho) is intentionally
 *   not used — it differs from the hint [0,1,0] for upright cameras and
 *   passing it to cam.camera() shifts orbitControl's orbit reference.
 *   Use capturePose() (p5.tree bridge) when the real up hint is needed.
 *
 * eval() writes { eye, center, up, fov, halfHeight }:
 *   eye        — Hermite (auto-CR when no tangents stored) or linear or step
 *   center     — Hermite (auto-CR when no tangents stored) or linear or step
 *   up         — nlerp (normalize-after-lerp on unit sphere)
 *   fov        — lerp when both keyframes carry non-null fov; else null
 *   halfHeight — lerp when both keyframes carry non-null halfHeight; else null
 *
 * @example
 * const track = new CameraTrack()
 * track.add({ eye:[0,0,500] })                 // center defaults to [0,0,0]
 * track.add({ eye:[300,-150,0], center:[0,0,0] })
 * track.add({ eMatrix: myEyeMatrix })
 * track.add({ vMatrix: myViewMatrix })
 * track.play({ loop: true })
 * // per frame:
 * track.tick()
 * const out = { eye:[0,0,0], center:[0,0,0], up:[0,1,0] }
 * track.eval(out)
 * cam.camera(out.eye[0],out.eye[1],out.eye[2],
 *            out.center[0],out.center[1],out.center[2],
 *            out.up[0],out.up[1],out.up[2])
 */
class CameraTrack extends Track {
  constructor() {
    super();
    /**
     * Eye position interpolation mode.
     * - 'hermite' — cubic Hermite; auto-CR tangents when none stored (default)
     * - 'linear'  — lerp
     * - 'step'    — snap to k0 eye
     * @type {'hermite'|'linear'|'step'}
     */
    this.eyeInterp = 'hermite';
    /**
     * Center (lookat target) interpolation mode.
     * 'linear' suits fixed or predictably moving targets (default).
     * 'hermite' gives smoother paths when center is also flying freely.
     * - 'hermite' — cubic Hermite; auto-CR tangents when none stored
     * - 'linear'  — lerp
     * - 'step'    — snap to k0 center
     * @type {'hermite'|'linear'|'step'}
     */
    this.centerInterp = 'linear';
    // Scratch for toCamera() — avoids hot-path allocations
    this._eye    = [0,0,0];
    this._center = [0,0,0];
    this._up     = [0,1,0];
  }

  /**
   * Append one or more camera keyframes. Adjacent duplicates are skipped by default.
   *
   * @param {Object|Object[]} spec
   *   { eye, center?, up? }  or  { vMatrix: mat4 }  or  { eMatrix: mat4 }  or  an array of either.
   * @param {{ deduplicate?: boolean }} [opts]
   */
  add(spec, opts) {
    if (Array.isArray(spec)) {
      for (const s of spec) this.add(s, opts);
      return;
    }
    const kf = _parseCameraSpec(spec);
    if (!kf) return;
    const dedup = !opts || opts.deduplicate !== false;
    if (dedup && this.keyframes.length > 0) {
      if (_sameCameraKeyframe(this.keyframes[this.keyframes.length - 1], kf)) return;
    }
    this.keyframes.push(kf);
  }

  /**
   * Replace (or append at end) the keyframe at index.
   * @param {number} index
   * @param {Object} spec
   * @returns {boolean}
   */
  set(index, spec) {
    if (!_isNum(index)) return false;
    const i = index | 0, kf = _parseCameraSpec(spec);
    if (!kf || i < 0 || i > this.keyframes.length) return false;
    if (i === this.keyframes.length) this.keyframes.push(kf);
    else this.keyframes[i] = kf;
    return true;
  }

  /**
   * Evaluate interpolated camera pose at current cursor.
   *
   * @param {{ eye:number[], center:number[], up:number[] }} [out]
   * @returns {{ eye:number[], center:number[], up:number[] }} out
   */
  eval(out) {
    out = out || { eye:[0,0,0], center:[0,0,0], up:[0,1,0], fov:null, halfHeight:null };
    const n = this.keyframes.length;
    if (n === 0) return out;

    if (n === 1) {
      const k = this.keyframes[0];
      out.eye[0]=k.eye[0];       out.eye[1]=k.eye[1];       out.eye[2]=k.eye[2];
      out.center[0]=k.center[0]; out.center[1]=k.center[1]; out.center[2]=k.center[2];
      out.up[0]=k.up[0];         out.up[1]=k.up[1];         out.up[2]=k.up[2];
      out.fov        = k.fov;
      out.halfHeight = k.halfHeight;
      return out;
    }

    const nSeg = n - 1;
    const dur  = Math.max(1, this.duration | 0);
    const seg  = _clampS(this.seg, 0, nSeg - 1);
    const t    = _clamp01(this.f / dur);
    const k0   = this.keyframes[seg];
    const k1   = this.keyframes[seg + 1];

    // eye — Hermite (auto-CR tangents when none stored), linear, or step
    if (this.eyeInterp === 'step') {
      out.eye[0]=k0.eye[0]; out.eye[1]=k0.eye[1]; out.eye[2]=k0.eye[2];
    } else if (this.eyeInterp === 'linear') {
      lerpVec3(out.eye, k0.eye, k1.eye, t);
    } else {
      const p0 = seg > 0      ? this.keyframes[seg - 1].eye : k0.eye;
      const p3 = seg + 2 < n ? this.keyframes[seg + 2].eye  : k1.eye;
      const m0 = k0.eyeTanOut != null ? k0.eyeTanOut
               : k0.eyeTanIn  != null ? k0.eyeTanIn
               : _crTanOut(_m0, p0, k0.eye, k1.eye, p3);
      const m1 = k1.eyeTanIn  != null ? k1.eyeTanIn
               : k1.eyeTanOut != null ? k1.eyeTanOut
               : _crTanIn(_m1, p0, k0.eye, k1.eye, p3);
      hermiteVec3(out.eye, k0.eye, m0, k1.eye, m1, t);
    }

    // center — Hermite, linear, or step (independent lookat target)
    if (this.centerInterp === 'step') {
      out.center[0]=k0.center[0]; out.center[1]=k0.center[1]; out.center[2]=k0.center[2];
    } else if (this.centerInterp === 'linear') {
      lerpVec3(out.center, k0.center, k1.center, t);
    } else {
      const c0 = seg > 0      ? this.keyframes[seg - 1].center : k0.center;
      const c3 = seg + 2 < n ? this.keyframes[seg + 2].center  : k1.center;
      const m0 = k0.centerTanOut != null ? k0.centerTanOut
               : k0.centerTanIn  != null ? k0.centerTanIn
               : _crTanOut(_m0, c0, k0.center, k1.center, c3);
      const m1 = k1.centerTanIn  != null ? k1.centerTanIn
               : k1.centerTanOut != null ? k1.centerTanOut
               : _crTanIn(_m1, c0, k0.center, k1.center, c3);
      hermiteVec3(out.center, k0.center, m0, k1.center, m1, t);
    }

    // up — nlerp (normalize after lerp; correct for typical near-upright cameras)
    const ux = k0.up[0] + t*(k1.up[0]-k0.up[0]);
    const uy = k0.up[1] + t*(k1.up[1]-k0.up[1]);
    const uz = k0.up[2] + t*(k1.up[2]-k0.up[2]);
    const ul = Math.sqrt(ux*ux+uy*uy+uz*uz) || 1;
    out.up[0]=ux/ul; out.up[1]=uy/ul; out.up[2]=uz/ul;

    // fov — lerp (perspective); null when either keyframe lacks it
    out.fov        = (k0.fov        !== null && k1.fov        !== null)
      ? k0.fov        + t * (k1.fov        - k0.fov)        : null;
    // halfHeight — lerp (ortho); null when either keyframe lacks it
    out.halfHeight = (k0.halfHeight !== null && k1.halfHeight !== null)
      ? k0.halfHeight + t * (k1.halfHeight - k0.halfHeight) : null;

    return out;
  }
}

/**
 * @file Frustum planes and visibility tests — zero allocations.
 * @module tree/visibility
 * @license AGPL-3.0-only
 *
 * Planes are a flat Float64Array(24): 6 planes × 4 floats [a, b, c, d].
 * All inputs are scalars. All outputs are INVISIBLE | VISIBLE | SEMIVISIBLE.
 */


// Plane indices
const PLANE_LEFT = 0, PLANE_RIGHT = 1, PLANE_NEAR = 2,
             PLANE_FAR = 3, PLANE_TOP = 4, PLANE_BOTTOM = 5;

/**
 * Compute 6 frustum planes from camera basis (world space) + projection params.
 * All inputs are scalars — the addon extracts them from the inverse view matrix
 * and projection queries before calling.
 *
 * @param {Float64Array} out  24-float output.
 * @param {number} posX,posY,posZ  Camera world position.
 * @param {number} vdX,vdY,vdZ    View direction (−Z in eye space, world).
 * @param {number} upX,upY,upZ    Camera up.
 * @param {number} rtX,rtY,rtZ    Camera right.
 * @param {boolean} ortho         true if orthographic.
 * @param {number} near,far,left,right,top,bottom  Projection plane values.
 */
function frustumPlanes(
  out,
  posX, posY, posZ,
  vdX, vdY, vdZ,
  upX, upY, upZ,
  rtX, rtY, rtZ,
  ortho,
  near, far, left, right, top, bottom
) {
  const posViewDir = posX*vdX + posY*vdY + posZ*vdZ;
  const posRight   = posX*rtX + posY*rtY + posZ*rtZ;
  const posUp      = posX*upX + posY*upY + posZ*upZ;

  if (ortho) {
    // Left: normal = −right
    out[0] = -rtX; out[1] = -rtY; out[2] = -rtZ;
    out[3] = -(posRight - left) * -1; // dot(pos - right*left, -right) ... simplified:
    // Actually: d = dot(pointOnPlane, normal)
    // pointOnPlane = pos + right*left (left is negative for standard ortho)
    // normal = -right
    // d = dot(pos + right*left, -right) = -posRight - left
    out[3] = -posRight - left;

    // Right: normal = right
    out[4] = rtX; out[5] = rtY; out[6] = rtZ;
    out[7] = posRight + right;

    // Top: normal = up
    out[16] = upX; out[17] = upY; out[18] = upZ;
    out[19] = posUp - bottom; // note: p5 top/bottom are swapped in sign convention

    // Bottom: normal = -up
    out[20] = -upX; out[21] = -upY; out[22] = -upZ;
    out[23] = -posUp + top;
  } else {
    // Left
    const hfovl = Math.atan2(left, near);
    const shfovl = Math.sin(hfovl), chfovl = Math.cos(hfovl);
    out[0] = vdX*shfovl - rtX*chfovl;
    out[1] = vdY*shfovl - rtY*chfovl;
    out[2] = vdZ*shfovl - rtZ*chfovl;
    out[3] = shfovl*posViewDir - chfovl*posRight;

    // Right
    const hfovr = Math.atan2(right, near);
    const shfovr = Math.sin(hfovr), chfovr = Math.cos(hfovr);
    out[4] = -vdX*shfovr + rtX*chfovr;
    out[5] = -vdY*shfovr + rtY*chfovr;
    out[6] = -vdZ*shfovr + rtZ*chfovr;
    out[7] = -shfovr*posViewDir + chfovr*posRight;

    // Top
    const fovt = Math.atan2(top, near);
    const sfovt = Math.sin(fovt), cfovt = Math.cos(fovt);
    out[16] = -vdX*sfovt + upX*cfovt;
    out[17] = -vdY*sfovt + upY*cfovt;
    out[18] = -vdZ*sfovt + upZ*cfovt;
    out[19] = -sfovt*posViewDir + cfovt*posUp;

    // Bottom
    const fovb = Math.atan2(bottom, near);
    const sfovb = Math.sin(fovb), cfovb = Math.cos(fovb);
    out[20] = vdX*sfovb - upX*cfovb;
    out[21] = vdY*sfovb - upY*cfovb;
    out[22] = vdZ*sfovb - upZ*cfovb;
    out[23] = sfovb*posViewDir - cfovb*posUp;
  }

  // Near plane: normal = −viewDir
  out[8]  = -vdX; out[9]  = -vdY; out[10] = -vdZ;
  out[11] = -posViewDir - near;

  // Far plane: normal = viewDir
  out[12] = vdX; out[13] = vdY; out[14] = vdZ;
  out[15] = posViewDir + far;

  return out;
}

/**
 * Signed distance from point to one frustum plane.
 * @param {Float64Array} planes  24-float planes buffer.
 * @param {number} planeIdx  0–5 (LEFT, RIGHT, NEAR, FAR, TOP, BOTTOM).
 * @param {number} px,py,pz  Point coordinates.
 * @returns {number}
 */
function distanceToPlane(planes, planeIdx, px, py, pz) {
  const b = planeIdx * 4;
  return planes[b]*px + planes[b+1]*py + planes[b+2]*pz - planes[b+3];
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function pointVisibility(planes, px, py, pz) {
  for (let i = 0; i < 6; i++) {
    if (distanceToPlane(planes, i, px, py, pz) > 0) return INVISIBLE;
  }
  return VISIBLE;
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function sphereVisibility(planes, cx, cy, cz, radius) {
  let allIn = true;
  for (let i = 0; i < 6; i++) {
    const d = distanceToPlane(planes, i, cx, cy, cz);
    if (d > radius) return INVISIBLE;
    if (d > 0 || -d < radius) allIn = false;
  }
  return allIn ? VISIBLE : SEMIVISIBLE;
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function boxVisibility(planes, x0, y0, z0, x1, y1, z1) {
  let allIn = true;
  for (let i = 0; i < 6; i++) {
    const b = i * 4;
    const a = planes[b], bv = planes[b+1], c = planes[b+2], d = planes[b+3];
    let allOut = true;
    for (let corner = 0; corner < 8; corner++) {
      const cx = (corner & 4) ? x0 : x1;
      const cy = (corner & 2) ? y0 : y1;
      const cz = (corner & 1) ? z0 : z1;
      const dist = a*cx + bv*cy + c*cz - d;
      if (dist > 0) { allIn = false; }
      else { allOut = false; }
    }
    if (allOut) return INVISIBLE;
  }
  return allIn ? VISIBLE : SEMIVISIBLE;
}

export { CameraTrack, EYE, INVISIBLE, MATRIX, MODEL, NDC, ORIGIN, PLANE_BOTTOM, PLANE_FAR, PLANE_LEFT, PLANE_NEAR, PLANE_RIGHT, PLANE_TOP, PoseTrack, SCREEN, SEMIVISIBLE, VISIBLE, WEBGL, WEBGPU, WORLD, _i, _j, _k, applyPickMatrix, boxVisibility, distanceToPlane, frustumPlanes, hermiteVec3, i, j, k, lerpVec3, mapDirection, mapLocation, mat3Direction, mat3NormalFromMat4, mat4Invert, mat4Location, mat4MV, mat4Mul, mat4MulPoint, mat4PV, mat4ToTransform, mat4Transpose, pixelRatio, pointVisibility, projBottom, projFar, projFov, projHfov, projIsOrtho, projLeft, projNear, projRight, projTop, qCopy, qDot, qFromAxisAngle, qFromLookDir, qFromMat4, qFromRotMat3x3, qMul, qNegate, qNlerp, qNormalize, qSet, qSlerp, qToMat4, quatToAxisAngle, sphereVisibility, transformToMat4 };
//# sourceMappingURL=index.js.map
